import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { ZoomIn, ZoomOut, RotateCcw, MapPin, Trash2, Eye, EyeOff, Camera, Save, Loader2, ChevronDown, Check, Expand, Shrink, ChevronLeft, ChevronRight } from 'lucide-react'
import type { PhotoVersion } from '@/services/turbineService'
import { useLanguage } from '@/contexts/LanguageContext'
import ErrorBanner from '@/components/ErrorBanner'
import { logError, toUserMessage } from '@/utils/errorMessage'

/**
 * Canvas asamasinda uretilen, mesaji ZATEN kullaniciya gosterilebilir olan hata.
 * Sunucudan gelen ApiError'dan ayirt edebilmek icin ayri bir tur: boylece
 * "Gorsel yuklenemedi" gibi somut sebep jenerik metinle ezilmez.
 */
class ImageSaveError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ImageSaveError'
    Object.setPrototypeOf(this, ImageSaveError.prototype)
  }
}

export interface Annotation {
  id: string
  x: number // percentage position
  y: number // percentage position
  label: string
  color: string
  createdAt: Date
}

interface ImageViewerWithAnnotationsProps {
  imageUrl: string | null
  imageName?: string
  imageInfo?: string
  onAnnotationsChange?: (annotations: Annotation[]) => void
  onSave?: (imageBlob: Blob, annotations: Annotation[]) => Promise<void>
  saving?: boolean
  onImageError?: () => void
  initialAnnotations?: Annotation[]
  themeColor?: 'green' | 'purple' | 'orange' | 'blue'
  versionOptions?: PhotoVersion[]
  currentVersionIndex?: number
  onVersionChange?: (index: number) => void
  onDeleteVersion?: (index: number, version: PhotoVersion) => void
  versionsLoading?: boolean
  currentUser?: any
  /** Prev/next navigation (fullscreen arrows + keyboard). Hidden when undefined. */
  onNavigate?: ((direction: 'prev' | 'next') => void) | undefined
}

const ANNOTATION_COLORS: string[] = [
  '#EF4444', // red
  '#F59E0B', // amber
  '#10B981', // emerald
  '#3B82F6', // blue
  '#8B5CF6', // violet
  '#EC4899', // pink
]

const DEFAULT_COLOR = '#EF4444'

const ImageViewerWithAnnotations: React.FC<ImageViewerWithAnnotationsProps> = ({
  imageUrl,
  imageName,
  imageInfo,
  onAnnotationsChange,
  onSave,
  saving = false,
  onImageError: _onImageError,
  initialAnnotations,
  themeColor = 'blue',
  versionOptions = [],
  currentVersionIndex = 0,
  onVersionChange,
  onDeleteVersion,
  versionsLoading = false,
  currentUser,
  onNavigate
}) => {
  const { t } = useLanguage()
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const errorCountRef = useRef<number>(0)
  const lastErrorUrlRef = useRef<string | null>(null)
  const versionDropdownRef = useRef<HTMLDivElement>(null)

  // Normalize incoming annotations once per change
  const normalizedInitialAnnotations = useMemo(() => {
    return (initialAnnotations || []).map((ann) => ({
      ...ann,
      createdAt: ann.createdAt instanceof Date ? ann.createdAt : new Date(ann.createdAt)
    }))
  }, [initialAnnotations])

  // Zoom and pan state
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  // Annotation state
  const [annotations, setAnnotations] = useState<Annotation[]>(normalizedInitialAnnotations)
  const [isAnnotationMode, setIsAnnotationMode] = useState(false)
  const [showAnnotations, setShowAnnotations] = useState(true)
  const [selectedAnnotation, setSelectedAnnotation] = useState<string | null>(null)
  const [newAnnotationLabel, setNewAnnotationLabel] = useState('')
  const [pendingAnnotation, setPendingAnnotation] = useState<{ x: number; y: number } | null>(null)
  const [currentColor, setCurrentColor] = useState<string>(DEFAULT_COLOR)
  const [isPreparingSave, setIsPreparingSave] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isVersionMenuOpen, setIsVersionMenuOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  // Measured max size for the image: percentage max-width/height can't reach the
  // img through the auto-sized zoom/pan wrappers, so measure the display area
  // instead and pass both dimensions down as pixel limits. Capping BOTH width and
  // height makes the photo fit like object-contain for ANY aspect ratio (portrait,
  // landscape or panorama) — it stays centered and never overflows/shifts.
  const [maxImgHeight, setMaxImgHeight] = useState<number | null>(null)
  const [maxImgWidth, setMaxImgWidth] = useState<number | null>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => {
      const styles = getComputedStyle(el)
      const padY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)
      const padX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight)
      setMaxImgHeight(Math.max(0, el.clientHeight - padY))
      setMaxImgWidth(Math.max(0, el.clientWidth - padX))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Fullscreen handling for the image display area
  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current
    if (!el) return

    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error('Error exiting fullscreen:', err)
      })
    } else {
      el.requestFullscreen().catch((err) => {
        console.error('Error entering fullscreen:', err)
      })
    }
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Keyboard navigation while in fullscreen (← →)
  useEffect(() => {
    if (!isFullscreen || !onNavigate) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        onNavigate('prev')
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        onNavigate('next')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen, onNavigate])

  useEffect(() => {
    if (!isVersionMenuOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (versionDropdownRef.current && !versionDropdownRef.current.contains(event.target as Node)) {
        setIsVersionMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isVersionMenuOpen])
  // Keep cache-busting params out of signed URLs (S3/Wasabi) to avoid breaking signatures
  const buildProxyUrl = useCallback((rawUrl: string) => {
    let cacheBust: string | null = null
    let cleanedUrl = rawUrl

    try {
      const parsed = new URL(rawUrl)
      cacheBust = parsed.searchParams.get('cb')
      if (cacheBust) {
        parsed.searchParams.delete('cb')
      }
      cleanedUrl = parsed.toString()
    } catch (error) {
      // Basic fallback for non-standard URLs
      const match = rawUrl.match(/[?&]cb=([^&]+)/)
      cacheBust = match?.[1] || null
      if (match) {
        cleanedUrl = rawUrl.replace(match[0], '').replace(/[?&]$/, '')
      }
    }

    const params = new URLSearchParams({ url: cleanedUrl })
    if (cacheBust) {
      params.set('cb', cacheBust)
    }

    return `/api/proxy-image?${params.toString()}`
  }, [])
  const proxyImageUrl = useMemo(() => {
    if (!imageUrl) return ''
    return buildProxyUrl(imageUrl)
  }, [buildProxyUrl, imageUrl])

  const getOutputFormat = useCallback(() => {
    const source = (imageName || imageUrl || '').toLowerCase()
    if (source.includes('.png')) return { mime: 'image/png', quality: undefined as number | undefined }
    if (source.includes('.webp')) return { mime: 'image/webp', quality: 0.92 }
    return { mime: 'image/jpeg', quality: 0.92 }
  }, [imageName, imageUrl])

  // Theme colors
  const themeColors = {
    green: { bg: 'bg-green-500', hover: 'hover:bg-green-600', text: 'text-green-600', border: 'border-green-300', light: 'bg-green-50' },
    purple: { bg: 'bg-purple-500', hover: 'hover:bg-purple-600', text: 'text-purple-600', border: 'border-purple-300', light: 'bg-purple-50' },
    orange: { bg: 'bg-orange-500', hover: 'hover:bg-orange-600', text: 'text-orange-600', border: 'border-orange-300', light: 'bg-orange-50' },
    blue: { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', text: 'text-blue-600', border: 'border-blue-300', light: 'bg-blue-50' }
  }
  const theme = themeColors[themeColor]

  // Reset state when image changes
  useEffect(() => {
    console.log('🖼️ ImageViewer: imageUrl changed:', imageUrl)
    console.log('🖼️ ImageViewer: imageUrl type:', typeof imageUrl)
    console.log('🖼️ ImageViewer: imageUrl length:', imageUrl?.length)
    console.log('🖼️ ImageViewer: imageName:', imageName)
    // Reset error tracking when URL changes
    errorCountRef.current = 0
    lastErrorUrlRef.current = null

    setZoom(1)
    setPosition({ x: 0, y: 0 })
    setAnnotations(normalizedInitialAnnotations)
    setSelectedAnnotation(null)
    setPendingAnnotation(null)
    setSaveError(null)
    setIsPreparingSave(false)
    setImageLoaded(false)
  }, [imageUrl, normalizedInitialAnnotations, imageName])

  // Notify parent of annotation changes
  useEffect(() => {
    onAnnotationsChange?.(annotations)
  }, [annotations, onAnnotationsChange])

  // Zoom handlers - No limit for zoom in, minimum 85% for zoom out
  const handleZoomIn = useCallback(() => {
    setZoom(prev => prev + 0.1)
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 0.1, 0.85)
      // Reset position when zooming out below 1.0 (no pan needed)
      if (newZoom <= 1.0 && (position.x !== 0 || position.y !== 0)) {
        setPosition({ x: 0, y: 0 })
      }
      return newZoom
    })
  }, [position])

  const handleResetView = useCallback(() => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }, [])

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isAnnotationMode) return
    if (zoom > 1) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
    }
  }, [zoom, position, isAnnotationMode])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoom > 1 && !isAnnotationMode) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }, [isDragging, zoom, dragStart, isAnnotationMode])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Annotation handlers
  const handleImageClick = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    if (!isAnnotationMode || !imageRef.current) return

    const rect = imageRef.current.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100

    setPendingAnnotation({ x, y })
    setNewAnnotationLabel('')
  }, [isAnnotationMode])

  const handleAddAnnotation = useCallback(() => {
    if (!pendingAnnotation || !newAnnotationLabel.trim()) return

    const newAnnotation: Annotation = {
      id: `ann-${Date.now()}`,
      x: pendingAnnotation.x,
      y: pendingAnnotation.y,
      label: newAnnotationLabel.trim(),
      color: currentColor,
      createdAt: new Date()
    }

    setAnnotations(prev => [...prev, newAnnotation])
    setPendingAnnotation(null)
    setNewAnnotationLabel('')
  }, [pendingAnnotation, newAnnotationLabel, currentColor])

  const handleDeleteAnnotation = useCallback((id: string) => {
    setAnnotations(prev => prev.filter(a => a.id !== id))
    setSelectedAnnotation(null)
  }, [])

  const handleCancelAnnotation = useCallback(() => {
    setPendingAnnotation(null)
    setNewAnnotationLabel('')
  }, [])

  const generateAnnotatedImageBlob = useCallback(async () => {
    if (!imageUrl) {
      throw new ImageSaveError('Kaydedilecek bir görsel bulunamadı') // TODO(i18n)
    }

    const img = new Image()
    // Load image through backend proxy to avoid CORS issues
    img.src = buildProxyUrl(imageUrl)

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new ImageSaveError('Görsel yüklenemedi')) // TODO(i18n)
    })

    const width = img.naturalWidth || img.width
    const height = img.naturalHeight || img.height

    if (!width || !height) {
      throw new ImageSaveError('Görsel boyutları okunamadı') // TODO(i18n)
    }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new ImageSaveError('Canvas desteklenmiyor') // TODO(i18n)
    }

    ctx.drawImage(img, 0, 0, width, height)

    annotations.forEach((annotation) => {
      const x = (annotation.x / 100) * width
      const y = (annotation.y / 100) * height

      const markerRadius = Math.max(8, width * 0.012)
      const markerOutline = Math.max(2, markerRadius * 0.35)

      ctx.fillStyle = annotation.color || DEFAULT_COLOR
      ctx.beginPath()
      ctx.arc(x, y, markerRadius, 0, Math.PI * 2)
      ctx.fill()
      ctx.lineWidth = markerOutline
      ctx.strokeStyle = '#ffffff'
      ctx.stroke()

      const fontSize = Math.max(14, width * 0.013)
      ctx.font = `600 ${fontSize}px Arial, sans-serif`
      ctx.textBaseline = 'top'
      const padding = fontSize * 0.45
      const text = annotation.label
      const textMetrics = ctx.measureText(text)
      const boxWidth = textMetrics.width + padding * 2
      const boxHeight = fontSize + padding * 2

      let boxX = x + markerRadius * 1.6
      let boxY = y - boxHeight / 2

      if (boxX + boxWidth > width - 8) boxX = width - boxWidth - 8
      if (boxY < 8) boxY = 8
      if (boxY + boxHeight > height - 8) boxY = height - boxHeight - 8

      ctx.fillStyle = 'rgba(17, 24, 39, 0.82)'
      ctx.fillRect(boxX, boxY, boxWidth, boxHeight)
      ctx.fillStyle = '#ffffff'
      ctx.fillText(text, boxX + padding, boxY + padding)
    })

    return await new Promise<Blob>((resolve, reject) => {
      const { mime, quality } = getOutputFormat()
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new ImageSaveError('Görsel kaydedilemedi')) // TODO(i18n)
        }
      }, mime, quality)
    })
  }, [annotations, buildProxyUrl, getOutputFormat, imageUrl])

  const handleSaveImage = useCallback(async () => {
    if (!onSave || !imageUrl) return
    setSaveError(null)
    setIsPreparingSave(true)

    try {
      // Generate annotated image blob (Canvas is NOT tainted because of proxy!)
      const annotatedBlob = await generateAnnotatedImageBlob()
      await onSave(annotatedBlob, annotations)
    } catch (error) {
      logError('ImageViewer.handleSaveImage', error)
      // Yerel canvas hatalarinin metni zaten TR ve aciklayici; sunucu tarafi
      // hatalari (onSave) ApiError olarak gelir ve backend mesajini tasir.
      setSaveError(
        error instanceof ImageSaveError
          ? error.message
          : toUserMessage(error, t, 'Fotoğraf kaydedilemedi') // TODO(i18n)
      )
    } finally {
      setIsPreparingSave(false)
    }
  }, [annotations, generateAnnotatedImageBlob, imageUrl, onSave, t])

  const getVersionLabel = useCallback((version: PhotoVersion | undefined, index: number) => {
    if (!version) return t.turbineDetail?.photos?.version || 'Version'
    if (version.is_original || index === 0) {
      return t.turbineDetail?.photos?.original || 'Original'
    }
    const editLabel = t.turbineDetail?.photos?.edit || 'Edit'
    const versionNumber = version.version_number ?? index
    return `${editLabel} ${versionNumber}`
  }, [t])

  const formatVersionMeta = useCallback((version: PhotoVersion) => {
    const parts: string[] = []
    if (version.created_by) parts.push(version.created_by)
    if (version.created_at) {
      const createdDate = new Date(version.created_at)
      if (!isNaN(createdDate.getTime())) {
        // Format: "21 Aralık 2025 - 14:50"
        const formattedDate = createdDate.toLocaleDateString('tr-TR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })
        const timeString = createdDate.toLocaleTimeString('tr-TR', {
          hour: '2-digit',
          minute: '2-digit'
        })
        parts.push(`${formattedDate} - ${timeString}`)
      }
    }
    return parts.join(' • ')
  }, [])

  // Empty state
  if (!imageUrl) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">{t.turbineDetail?.photos?.selectPhoto || 'Select photo'}</p>
          <p className="text-gray-400 text-sm mt-2 max-w-sm mx-auto">
            {t.turbineDetail?.photos?.selectPhotoDescription || ''}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 bg-gray-100 flex flex-col relative">
      {/* Photo Info Bar */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900">{imageName || 'Unnamed Photo'}</h4>
            {imageInfo && <p className="text-sm text-gray-500">{imageInfo}</p>}
        </div>

        {/* Toolbar */}
        <div className="flex items-center space-x-2">
          {versionOptions.length > 1 && onVersionChange && (
            <div className="relative" ref={versionDropdownRef}>
              <button
                type="button"
                onClick={() => !versionsLoading && setIsVersionMenuOpen((prev) => !prev)}
                disabled={versionsLoading}
                className={`flex items-center space-x-3 px-3 py-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-gray-300 transition-colors ${
                  versionsLoading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-[0.08em] text-gray-500 font-semibold leading-tight">
                    {t.turbineDetail?.photos?.version || 'Version'}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 leading-tight">
                    {getVersionLabel(versionOptions[currentVersionIndex], currentVersionIndex)}
                  </p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-500 transition-transform ${isVersionMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isVersionMenuOpen && (
                <div className="absolute left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden min-w-[280px]">
                  <ul className="py-1 overflow-y-auto" style={{ maxHeight: '320px' }}>
                    {versionOptions.map((version, idx) => {
                      const isActive = idx === currentVersionIndex
                      const isSuperAdmin = currentUser?.role === 'superadmin'
                      const canDelete = isSuperAdmin && idx > 0 // Orijinal versiyonu silemesin

                      return (
                        <li key={`${version.id}-${idx}`}>
                          <div className={`flex items-start justify-between gap-3 px-3 py-2.5 ${
                            isActive ? 'bg-gray-100' : 'hover:bg-gray-50'
                          }`}>
                            <button
                              type="button"
                              className="flex-1 text-left"
                              onClick={() => {
                                setIsVersionMenuOpen(false)
                                onVersionChange(idx)
                              }}
                            >
                              <div>
                                <p className="text-sm font-semibold text-gray-900 leading-snug">
                                  {getVersionLabel(version, idx)}
                                </p>
                                {(version.created_at || version.created_by) && (
                                  <p className="text-xs text-gray-500 mt-0.5 leading-tight">
                                    {formatVersionMeta(version)}
                                  </p>
                                )}
                              </div>
                            </button>
                            <div className="flex items-center gap-2">
                              {isActive && <Check className="w-4 h-4 text-green-600" />}
                              {canDelete && onDeleteVersion && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onDeleteVersion(idx, version)
                                  }}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title="Bu versiyonu sil"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Zoom Controls */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1 space-x-1">
            <button
              onClick={handleZoomOut}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                title={t.turbineDetail?.photos?.zoomOut || 'Zoom Out'}
              >
                <ZoomOut className="w-4 h-4 text-gray-600" />
              </button>
              <span className="text-sm font-medium text-gray-600 px-2 min-w-[50px] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                title={t.turbineDetail?.photos?.zoomIn || 'Zoom In'}
              >
                <ZoomIn className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={handleResetView}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                title={t.turbineDetail?.photos?.resetView || 'Reset View'}
              >
                <RotateCcw className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-300" />

            {/* Annotation Controls */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setIsAnnotationMode(!isAnnotationMode)}
                className={`p-2 rounded-lg transition-colors flex items-center space-x-1 ${
                  isAnnotationMode
                    ? `${theme.bg} text-white`
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                }`}
                title={isAnnotationMode ? (t.turbineDetail?.photos?.exitAnnotationMode || 'Exit Annotation Mode') : (t.turbineDetail?.photos?.annotationMode || 'Annotation Mode')}
              >
                <MapPin className="w-4 h-4" />
                <span className="text-xs font-medium hidden sm:inline">
                  {isAnnotationMode ? (t.turbineDetail?.photos?.exitMode || 'Exit') : (t.turbineDetail?.photos?.addMarker || 'Add Marker')}
                </span>
              </button>

              <button
                onClick={() => setShowAnnotations(!showAnnotations)}
                className={`p-2 rounded-lg transition-colors ${
                  showAnnotations
                    ? 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    : 'bg-gray-300 text-gray-500'
                }`}
                title={showAnnotations ? (t.turbineDetail?.photos?.hideMarkers || 'Hide Markers') : (t.turbineDetail?.photos?.showMarkers || 'Show Markers')}
              >
                {showAnnotations ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>

            {onSave && (
              <>
                <div className="w-px h-8 bg-gray-300" />
                <button
                  onClick={handleSaveImage}
                  disabled={annotations.length === 0 || isPreparingSave || saving}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-white text-sm font-semibold shadow-sm transition-colors ${
                    annotations.length === 0 || isPreparingSave || saving
                      ? 'bg-gray-300 cursor-not-allowed'
                      : `${theme.bg} ${theme.hover}`
                  }`}
                  title={t.turbineDetail?.photos?.save || 'Save'}
                >
                  {isPreparingSave || saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{t.turbineDetail?.photos?.save || 'Save'}</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Image Display Area */}
      <div
        ref={containerRef}
        className={`flex-1 p-4 flex items-center justify-center overflow-hidden ${
          isFullscreen ? 'bg-black' : ''
        } ${
          isAnnotationMode ? 'cursor-crosshair' : (zoom > 1 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default')
        }`}
        style={{
          position: 'relative',
          touchAction: 'none',
          overscrollBehavior: 'contain' // stop page scroll when wheel-zooming on the image
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Prev / next arrows - fullscreen only */}
        {isFullscreen && onNavigate && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onNavigate('prev')
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-40 p-3 rounded-full bg-black/50 hover:bg-black/75 text-white border border-white/20 shadow-lg transition-colors"
              title="Önceki fotoğraf (←)"
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onNavigate('next')
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-40 p-3 rounded-full bg-black/50 hover:bg-black/75 text-white border border-white/20 shadow-lg transition-colors"
              title="Sonraki fotoğraf (→)"
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          </>
        )}

        {/* Photo name overlay - fullscreen only */}
        {isFullscreen && imageName && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-full bg-black/60 text-white text-sm font-medium border border-white/10">
            {imageName}
          </div>
        )}

        {/* Skeleton while the photo is loading */}
        {!imageLoaded && (
          <div className="absolute inset-4 rounded-lg bg-gray-200 animate-pulse" />
        )}

        <div
          style={{
            position: 'relative',
            transform: `translate(${position.x}px, ${position.y}px)`,
            willChange: 'transform'
          }}
        >
          {/* Fullscreen toggle - inside the image, slightly inset from its top-right corner.
              Hidden until the photo loads (the wrapper is 0x0 before that, which would
              leave the button floating in the middle of the area). */}
          {imageLoaded && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                toggleFullscreen()
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className={`absolute top-3 right-3 z-40 p-2.5 rounded-xl shadow-lg transition-colors ${
                isFullscreen
                  ? 'bg-black/60 hover:bg-black/80 text-white border border-white/20'
                  : 'bg-white/90 hover:bg-white text-gray-700 border border-gray-200'
              }`}
              title={isFullscreen ? (t.turbineDetail?.photos?.exitMode || 'Tam ekrandan çık') : 'Tam ekran'}
            >
              {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
            </button>
          )}
          <div
            className="relative inline-block"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'center center',
              willChange: 'transform'
            }}
          >
            {imageUrl && (
              <img
                key={imageUrl}
                ref={imageRef}
                src={proxyImageUrl}
                alt={imageName || 'Photo'}
                className={`object-contain rounded-lg shadow-lg select-none block transition-opacity duration-200 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                style={{
                  ...(maxImgHeight != null ? { maxHeight: maxImgHeight } : {}),
                  ...(maxImgWidth != null ? { maxWidth: maxImgWidth } : {})
                }}
                draggable={false}
                onClick={handleImageClick}
                onLoad={() => {
                  setImageLoaded(true)
                  console.log('✅ Image loaded successfully via proxy')
                }}
                onError={(e) => {
                  console.error('❌ Image failed to load via proxy:', imageUrl)
                  console.error('Error event:', e)
                }}
              />
            )}

            {/* Render Annotations */}
            {showAnnotations && annotations.map((annotation) => (
            <div
              key={annotation.id}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all ${
                selectedAnnotation === annotation.id ? 'scale-125 z-20' : 'z-10 hover:scale-110'
              }`}
              style={{
                left: `${annotation.x}%`,
                top: `${annotation.y}%`,
              }}
              onClick={(e) => {
                e.stopPropagation()
                setSelectedAnnotation(selectedAnnotation === annotation.id ? null : annotation.id)
              }}
            >
              {/* Marker */}
              <div
                className="w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center"
                style={{ backgroundColor: annotation.color }}
              >
                <MapPin className="w-3 h-3 text-white" />
              </div>

              {/* Label tooltip */}
              {selectedAnnotation === annotation.id && (
                <div className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-xl p-3 min-w-[150px] z-30">
                  <p className="text-sm font-medium text-gray-900 mb-2">{annotation.label}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {annotation.createdAt.toLocaleDateString('tr-TR')}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteAnnotation(annotation.id)
                      }}
                      className="p-1 hover:bg-red-100 rounded text-red-500"
                      title={t.turbineDetail?.photos?.deleteMarker || 'Delete Marker'}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Pending annotation marker */}
          {pendingAnnotation && (
            <div
              className="absolute transform -translate-x-1/2 -translate-y-1/2 z-30"
              style={{
                left: `${pendingAnnotation.x}%`,
                top: `${pendingAnnotation.y}%`,
              }}
            >
              <div
                className="w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center animate-pulse"
                style={{ backgroundColor: currentColor }}
              >
                <MapPin className="w-3 h-3 text-white" />
              </div>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Annotation Input Modal */}
      {pendingAnnotation && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-white rounded-xl shadow-2xl p-4 z-50 w-80">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            {t.turbineDetail?.photos?.addMarkerLabel || 'Add Marker Label'}
          </h4>

          {/* Color Selection */}
          <div className="flex items-center space-x-2 mb-3">
            <span className="text-xs text-gray-500">{t.turbineDetail?.photos?.markerColor || 'Color'}:</span>
            {ANNOTATION_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => setCurrentColor(color)}
                className={`w-5 h-5 rounded-full transition-transform ${
                  currentColor === color ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : ''
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>

          <input
            type="text"
            value={newAnnotationLabel}
            onChange={(e) => setNewAnnotationLabel(e.target.value)}
            placeholder={t.turbineDetail?.photos?.markerPlaceholder || 'Enter marker description...'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddAnnotation()
              }
              if (e.key === 'Escape') {
                e.preventDefault()
                handleCancelAnnotation()
              }
            }}
          />

          <div className="flex space-x-2">
            <button
              onClick={handleAddAnnotation}
              disabled={!newAnnotationLabel.trim()}
              className={`flex-1 py-2 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${theme.bg} ${theme.hover}`}
            >
              {t.turbineDetail?.photos?.save || 'Save'}
            </button>
            <button
              onClick={handleCancelAnnotation}
              className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 text-sm font-medium transition-colors"
            >
              {t.common?.cancel || 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Mode Indicator */}
      {isAnnotationMode && (
        <div className={`absolute top-20 left-1/2 transform -translate-x-1/2 ${theme.light} ${theme.border} border rounded-lg px-4 py-2 shadow-lg z-40`}>
          <p className={`text-sm font-medium ${theme.text}`}>
            {t.turbineDetail?.photos?.clickToAddMarker || 'Click on the image to add a marker'}
          </p>
        </div>
      )}

      {/* Annotations Count Badge */}
      {annotations.length > 0 && (
        <div className={`absolute top-20 right-4 ${theme.light} ${theme.border} border rounded-lg px-3 py-2 shadow-lg`}>
          <p className={`text-sm font-medium ${theme.text}`}>
            {annotations.length} {t.turbineDetail?.photos?.markers || 'marker(s)'}
          </p>
        </div>
      )}

      {saveError && (
        <div className="absolute bottom-4 right-4 z-40 max-w-xs">
          <ErrorBanner
            message={saveError}
            onDismiss={() => setSaveError(null)}
            onRetry={isPreparingSave || saving ? undefined : handleSaveImage}
            className="shadow-lg"
          />
        </div>
      )}
    </div>
  )
}

export default ImageViewerWithAnnotations
